import { adaptAgentServiceWebSocketNotification } from "./agent-service-contract.js";
import { acquireRealtime } from "./realtime-hub.js";
import {
  adaptCustomerLanguageNotification,
  adaptCustomerTranslationNotification,
  AGENT_SERVICE_LANGUAGE_CONTRACT_VERSION,
} from "./agent-service-language.js";



/**
 * 管理订单客服原生WebSocket连接。
 * Token只通过AUTH帧发送，不进入URL、日志或持久化状态。
 */
export class AgentServiceRealtimeClient {
  constructor(options) {
    this.token = options.token;
    this.conversationId = options.conversationId;
    this.presenceOnly = Boolean(options.presenceOnly);
    this.onNotification = options.onNotification || (() => {});
    this.onLanguageNotification = options.onLanguageNotification || (() => {});
    this.onStateChange = options.onStateChange || (() => {});
    this.onPresenceChange = options.onPresenceChange || (() => {});
    this.onError = options.onError || (() => {});
    // 需要恢复的会话窗口注入REST补读；仅在线状态订阅不读消息。
    this.onRefresh =
      typeof options.onRefresh === "function" ? options.onRefresh : null;
    this.WebSocketClass = options.WebSocketClass || window.WebSocket;
    this.locationLike = options.locationLike || window.location;
    this.lease = null;
    this.state = "CONNECTING";
    this.closedByUser = false;
    this.refreshTimer = null;
    this.refreshInFlight = null;
    this.receptionEpoch = 0;
    this.conversationMissing = false;
    this.visibilityTarget = options.visibilityTarget || globalThis.document;
    this.onVisibility = () => { if (!this.visibilityTarget?.hidden) void this.refreshAuthoritativeMessages(); };
  }

  /** 统一页面级连接承载Agent和真人客服；各窗口只释放自己的订阅。 */
  connect() {
    if (!this.token || (!this.conversationId && !this.presenceOnly) || this.lease || this.closedByUser) return;
    this.visibilityTarget?.addEventListener?.("visibilitychange", this.onVisibility);
    this.lease = acquireRealtime({
      token: this.token,
      WebSocketClass: this.WebSocketClass,
      locationLike: this.locationLike,
      subscriptions: [{ channel: "customer-service", resourceId: this.presenceOnly ? "self" : this.conversationId }],
      onState: state => {
        if (this.closedByUser) return;
        const wasConnected = this.state === "CONNECTED";
        this.state = state; this.onStateChange(state);
        if (wasConnected !== (state === "CONNECTED") || ["REJECTED", "CLOSED"].includes(state)) {
          clearTimeout(this.refreshTimer); this.refreshTimer = null;
        }
        this.scheduleRefresh();
      },
      onReady: () => this.refreshAuthoritativeMessages(),
      onEvent: frame => {
        if (this.presenceOnly) return;
        if (["resource.deleted", "resource.revoked"].includes(frame.eventType) || frame.payload?.type === "SYNC_RESET") return this.refreshAuthoritativeMessages();
        this.handleMessage(JSON.stringify(frame.payload));
      },
      onError: error => { if (!this.closedByUser) this.onError(error); },
    });
    this.scheduleRefresh();
  }

  /** 主动关闭连接并取消后续自动重连。 */
  close() {
    this.closedByUser = true;
    this.visibilityTarget?.removeEventListener?.("visibilitychange", this.onVisibility);
    this.receptionEpoch += 1;
    clearTimeout(this.refreshTimer);
    this.refreshTimer = null;
    this.refreshInFlight = null;
    this.lease?.close(); this.lease = null;
    this.token = "";
    this.onStateChange("CLOSED");
  }

  handleMessage(source) {
    if (this.closedByUser) return;
    try {
      const frame = JSON.parse(source);
      if (frame.type === "PRESENCE") {
        if (String(frame.conversationId || "") !== this.conversationId) {
          throw new Error("在线状态不属于当前会话。");
        }
        if (!frame.userId) throw new Error("在线状态缺少用户标识。");
        this.onPresenceChange({
          userId: String(frame.userId),
          online: Boolean(frame.online),
        });
        return;
      }
      if (frame.type === "ERROR") {
        throw new Error(frame.message || "客服实时连接被拒绝");
      }
      if (
        frame.contractVersion === AGENT_SERVICE_LANGUAGE_CONTRACT_VERSION
      ) {
        const notification =
          frame.type === "message.translation.updated"
            ? adaptCustomerTranslationNotification(frame)
            : adaptCustomerLanguageNotification(frame);
        if (notification.conversationId !== this.conversationId) {
          throw new Error("语言通知不属于当前会话。");
        }
        this.deliverNotification(this.onLanguageNotification, notification);
        return;
      }
      this.deliverNotification(
        this.onNotification,
        adaptAgentServiceWebSocketNotification(frame)
      );
    } catch (error) {
      this.onError(error);
      void this.refreshAuthoritativeMessages();
    }
  }

  /** 通知回调可能异步补读；接住其失败并恢复权威消息，避免Promise拒绝后永久保留转圈。 */
  deliverNotification(callback, notification) {
    const epoch = this.receptionEpoch;
    Promise.resolve()
      .then(() => {
        if (!this.closedByUser && epoch === this.receptionEpoch)
          return callback(notification);
      })
      .catch((error) => {
        if (this.closedByUser || epoch !== this.receptionEpoch) return;
        this.onError(error);
        void this.refreshAuthoritativeMessages();
      });
  }

  /** 补读只串行读取当前会话，不取消或重新发送问题；关闭后的响应由调用方会话代次屏障拦截。 */
  async refreshAuthoritativeMessages() {
    if (
      !this.onRefresh ||
      this.presenceOnly ||
      this.closedByUser ||
      this.conversationMissing || ["REJECTED", "CLOSED"].includes(this.state)
    )
      return;
    if (this.refreshInFlight) return this.refreshInFlight;
    const epoch = this.receptionEpoch;
    const operation = Promise.resolve()
      .then(() => {
        if (!this.closedByUser && epoch === this.receptionEpoch)
          return this.onRefresh();
      })
      .catch((error) => {
        if (this.closedByUser || epoch !== this.receptionEpoch) return;
        // 明确删除的会话不能持续补读或由客户端自动重建；普通网络失败下一轮继续恢复。
        if (
          error?.code === 404 ||
          error?.response?.status === 404 ||
          error?.errorCode === "CONVERSATION_NOT_FOUND" ||
          error?.response?.data?.errorCode === "CONVERSATION_NOT_FOUND"
        ) {
          this.conversationMissing = true;
        }
        this.onError(error);
      })
      .finally(() => {
        if (this.refreshInFlight === operation) this.refreshInFlight = null;
      });
    this.refreshInFlight = operation;
    return operation;
  }

  /** 正常连接低频校对，断线才降级补读；上次结束后再调度，慢请求不会堆积。 */
  scheduleRefresh() {
    if (
      !this.onRefresh ||
      this.presenceOnly ||
      this.closedByUser ||
      this.conversationMissing ||
      this.refreshTimer !== null || ["REJECTED", "CLOSED"].includes(this.state)
    )
      return;
    this.refreshTimer = setTimeout(async () => {
      this.refreshTimer = null;
      await this.refreshAuthoritativeMessages();
      this.scheduleRefresh();
    }, this.state === "CONNECTED" ? 60000 : 10000);
  }

}
